import { graphql, HttpResponse } from "msw";

import { waitForMockLatency } from "@/mocks/latency";
import { mockCarts } from "@/mocks/store";
import {
  addMockCartLines,
  createMockCart,
  serializeMockCart,
  serializeSellingPlanNode,
  setMockCartDiscountCodes,
  toArray,
  updateMockCartLines,
} from "@/mocks/storefront";
import type {
  CartCreateMutation,
  CartCreateMutationVariables,
  CartDiscountCodesUpdateMutation,
  CartDiscountCodesUpdateMutationVariables,
  CartLinesAddMutation,
  CartLinesAddMutationVariables,
  CartLinesUpdateMutation,
  CartLinesUpdateMutationVariables,
  GetCartQuery,
  GetCartQueryVariables,
  GetProductSellingPlansQuery,
  GetProductSellingPlansQueryVariables,
} from "@/types/storefront-api.gen";

type ShopifyHandlerOptions = {
  storefrontEndpoint: string;
  shopifyOrigin: string;
};

const cartNotFoundError = (cartId: string) => {
  return [{ field: ["cartId"], message: `The specified cart ${cartId} does not exist.` }];
};

export const createShopifyHandlers = ({
  shopifyOrigin,
  storefrontEndpoint,
}: ShopifyHandlerOptions) => {
  const storefront = graphql.link(storefrontEndpoint);

  return [
    storefront.query<GetProductSellingPlansQuery, GetProductSellingPlansQueryVariables>(
      "getProductSellingPlans",
      async ({ variables }) => {
        await waitForMockLatency();

        return HttpResponse.json({
          data: { nodes: toArray(variables.ids).map(serializeSellingPlanNode) },
        });
      },
    ),

    storefront.query<GetCartQuery, GetCartQueryVariables>("getCart", async ({ variables }) => {
      await waitForMockLatency();

      const cart = mockCarts.get(variables.cartId);

      return HttpResponse.json({
        data: { cart: cart ? serializeMockCart(cart, shopifyOrigin) : null },
      });
    }),

    storefront.mutation<CartCreateMutation, CartCreateMutationVariables>(
      "cartCreate",
      async ({ variables }) => {
        await waitForMockLatency();

        const { cart, userErrors } = createMockCart(variables.input);

        return HttpResponse.json({
          data: { cartCreate: { cart: serializeMockCart(cart, shopifyOrigin), userErrors } },
        });
      },
    ),

    storefront.mutation<CartLinesAddMutation, CartLinesAddMutationVariables>(
      "cartLinesAdd",
      async ({ variables }) => {
        await waitForMockLatency();

        const cart = mockCarts.get(variables.cartId);

        if (!cart) {
          return HttpResponse.json({
            data: {
              cartLinesAdd: {
                cart: null,
                userErrors: cartNotFoundError(variables.cartId),
                warnings: [],
              },
            },
          });
        }

        const userErrors = addMockCartLines(cart, toArray(variables.lines));

        return HttpResponse.json({
          data: {
            cartLinesAdd: {
              cart: serializeMockCart(cart, shopifyOrigin),
              userErrors,
              warnings: [],
            },
          },
        });
      },
    ),

    storefront.mutation<CartLinesUpdateMutation, CartLinesUpdateMutationVariables>(
      "cartLinesUpdate",
      async ({ variables }) => {
        await waitForMockLatency();

        const cart = mockCarts.get(variables.cartId);

        if (!cart) {
          return HttpResponse.json({
            data: {
              cartLinesUpdate: {
                cart: null,
                userErrors: cartNotFoundError(variables.cartId),
                warnings: [],
              },
            },
          });
        }

        const userErrors = updateMockCartLines(cart, toArray(variables.lines));

        return HttpResponse.json({
          data: {
            cartLinesUpdate: {
              cart: serializeMockCart(cart, shopifyOrigin),
              userErrors,
              warnings: [],
            },
          },
        });
      },
    ),

    storefront.mutation<CartDiscountCodesUpdateMutation, CartDiscountCodesUpdateMutationVariables>(
      "cartDiscountCodesUpdate",
      async ({ variables }) => {
        await waitForMockLatency();

        const cart = mockCarts.get(variables.cartId);

        if (!cart) {
          return HttpResponse.json({
            data: {
              cartDiscountCodesUpdate: {
                cart: null,
                userErrors: cartNotFoundError(variables.cartId),
              },
            },
          });
        }

        setMockCartDiscountCodes(cart, toArray(variables.discountCodes));

        return HttpResponse.json({
          data: {
            cartDiscountCodesUpdate: {
              cart: serializeMockCart(cart, shopifyOrigin),
              userErrors: [],
            },
          },
        });
      },
    ),
  ];
};
